/** @jsxRuntime classic */
/** @jsx jsx */
/** @jsxFrag React.Fragment */
import { gql } from '@apollo/client';
import { FormattedText, P, Image } from '@bottlebooks/gatsby-theme-base';
import useLocale from '@bottlebooks/gatsby-theme-event/src/useLocale';
import { Trans } from '@lingui/macro';
import { graphql } from 'gatsby';
import { jsx } from 'theme-ui';
import Section from '@bottlebooks/gatsby-theme-event/src/components/Section/Section';
import React from 'react';

/**
 *
 * @param {object} props
 * @param {object} props.brand
 */
export default function BrandWinemaker({ brand, ...rest }) {
  const { owner, winemaker, director } = useAfterQuery(brand);
  return (
    <>
      <Portrait title={<Trans>Owner</Trans>} person={owner} />
      <Portrait title={<Trans>Director</Trans>} person={director} />
      <Portrait title={<Trans>Winemaker</Trans>} person={winemaker} />
    </>
  );
}

/**
 *
 * @param {object} props
 * @param {object} props.person
 */
function Portrait({ person, title, ...rest }) {
  if (!person?.name && !person?.image && !person?.description) return null;
  return (
    <Section title={title} {...rest}>
      <P variant="title" sx={{ marginBottom: 3 }}>
        {person.name}
      </P>

      {person.image?.fluid && (
        <Image
          alt={person.name}
          fluid={person.image.fluid}
          sx={{ maxWidth: '100%' }}
        />
      )}
      <FormattedText
        text={person.description}
        variant="large"
        sx={{ marginTop: 3 }}
      />
    </Section>
  );
}

export const fragment = graphql`
  fragment vv_BrandWinemaker on Bottlebooks_SingleRegistration {
    profile {
      winemakerName
    }
    exhibitorPackage: customFieldValue(key: "exhibitorPackage") {
      ... on Bottlebooks_SelectFieldValue {
        key
      }
    }
    winemakerImage: customFieldValue(key: "winemakerImage") {
      ... on Bottlebooks_ImageFieldValue {
        value {
          fluid(
            maxWidth: 960
            maxHeight: 480
            crop: FILL
            gravity: FACES_AUTO
          ) {
            src
            srcSet
            sizes
            base64
            aspectRatio
          }
        }
      }
    }
    winemakerDescription_DE: customFieldValue(key: "winemakerDescription_DE") {
      ... on Bottlebooks_TextFieldValue {
        value
      }
    }
    winemakerDescription_EN: customFieldValue(key: "winemakerDescription_EN") {
      ... on Bottlebooks_TextFieldValue {
        value
      }
    }
  }

  fragment vv_BrandOwner on Bottlebooks_SingleRegistration {
    profile {
      ownerName
      directorName
    }

    ownerImage: customFieldValue(key: "ownerImage") {
      ... on Bottlebooks_ImageFieldValue {
        value {
          fluid(
            maxWidth: 960
            maxHeight: 480
            crop: FILL
            gravity: FACES_AUTO
          ) {
            src
            srcSet
            sizes
            base64
            aspectRatio
          }
        }
      }
    }
    ownerDescription_DE: customFieldValue(key: "ownerDescription_DE") {
      ... on Bottlebooks_TextFieldValue {
        value
      }
    }
    ownerDescription_EN: customFieldValue(key: "ownerDescription_EN") {
      ... on Bottlebooks_TextFieldValue {
        value
      }
    }
  }

  # Unused
  fragment vv_BrandDirector on Bottlebooks_SingleRegistration {
    profile {
      directorName
    }

    directorImage: customFieldValue(key: "directorImage") {
      ... on Bottlebooks_ImageFieldValue {
        value {
          fluid(
            maxWidth: 960
            maxHeight: 480
            crop: FILL
            gravity: FACES_AUTO
          ) {
            src
            srcSet
            sizes
            base64
            aspectRatio
          }
        }
      }
    }
    directorDescription_DE: customFieldValue(key: "directorDescription_DE") {
      ... on Bottlebooks_TextFieldValue {
        value
      }
    }
    directorDescription_EN: customFieldValue(key: "directorDescription_EN") {
      ... on Bottlebooks_TextFieldValue {
        value
      }
    }
  }
`;

export const BrandWinemakerFragment = gql`
  #   TODO: add winemakerImage
  fragment BrandWinemaker on AbstractBrand {
    ... on Exhibitor {
      winemakerName
      ownerName
    }
    ... on Producer {
      winemakerName
      ownerName
    }
  }
`;

function useAfterQuery(brand) {
  const locale = useLocale();
  const exhibitorPackage = brand?.exhibitorPackage?.key;
  const hasPremium =
    exhibitorPackage === 'premium' || exhibitorPackage === 'pavilion';
  return {
    owner: {
      name: brand?.profile?.ownerName,
      image: hasPremium && brand?.ownerImage?.value,
      description: hasPremium
        ? locale === 'de'
          ? brand?.ownerDescription_DE?.value
          : brand?.ownerDescription_EN?.value
        : '',
    },
    director: {
      name: brand?.profile?.directorName,
      image: hasPremium && brand?.directorImage?.value,
      description: hasPremium
        ? locale === 'de'
          ? brand?.directorDescription_DE?.value
          : brand?.directorDescription_EN?.value
        : '',
    },
    winemaker: {
      name: brand?.profile?.winemakerName,
      image: hasPremium && brand?.winemakerImage?.value,
      description: hasPremium
        ? locale === 'de'
          ? brand?.winemakerDescription_DE?.value
          : brand?.winemakerDescription_EN?.value
        : '',
    },
  };
}
